import React, { useEffect, useState } from 'react';
import { useSafeThemeClasses } from '@/shared/theme';
import {
  analyticsApiService,
  type UserGrowthData,
  type ActiveSessionMetrics,
} from '@/shared/services/analyticsApiService';
import { logger } from '@/shared/utils';

interface VisitorAnalytics {
  unique_visitors: number;
  total_page_views: number;
  unique_sessions: number;
  unique_users: number;
  avg_page_views_per_visitor: number;
}

interface TrafficSource {
  source_type: string;
  page_views: number;
  unique_sessions: number;
  unique_users: number;
  avg_time_on_page_seconds: number;
  percentage: number;
}

const AdminAnalyticsPage: React.FC = () => {
  const theme = useSafeThemeClasses();
  const [userCount, setUserCount] = useState<{ total: number; growth: number } | null>(null);
  const [activeSessions, setActiveSessions] = useState<ActiveSessionMetrics | null>(null);
  const [userGrowth, setUserGrowth] = useState<UserGrowthData | null>(null);
  const [visitors, setVisitors] = useState<VisitorAnalytics | null>(null);
  const [trafficSources, setTrafficSources] = useState<TrafficSource[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('month');

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      setError(null);
      try {
        try {
          const userCountData = await analyticsApiService.getUserCount(period);
          if (userCountData && userCountData.current_period) {
            setUserCount({
              total: userCountData.current_period.total_users || 0,
              growth: userCountData.growth?.total_users_percent || 0,
            });
          }
        } catch (err) {
          logger.error('Failed to load user count', 'AdminAnalyticsPage', { error: err });
          setUserCount(null);
        }

        try {
          const sessionsData = await analyticsApiService.getActiveSessions();
          setActiveSessions(sessionsData || null);
        } catch (err) {
          logger.error('Failed to load active sessions', 'AdminAnalyticsPage', { error: err });
          setActiveSessions(null);
        }

        try {
          const growthData = await analyticsApiService.getUserGrowth();
          setUserGrowth(growthData || null);
        } catch (err) {
          logger.error('Failed to load user growth', 'AdminAnalyticsPage', { error: err });
          setUserGrowth(null);
        }

        try {
          const visitorData = await analyticsApiService.getVisitorAnalytics({ period });
          setVisitors(visitorData || null);
        } catch (err) {
          logger.error('Failed to load visitor analytics', 'AdminAnalyticsPage', { error: err });
          setVisitors(null);
        }

        try {
          const trafficData = await analyticsApiService.getTrafficSources({ period });
          setTrafficSources(trafficData?.sources || null);
        } catch (err) {
          logger.error('Failed to load traffic sources', 'AdminAnalyticsPage', { error: err });
          setTrafficSources(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [period]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className={`${theme.card} rounded-xl p-12 text-center`}>
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.textAccent} mx-auto mb-4`}></div>
          <p className={theme.textSecondary}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className={`${theme.alertError} rounded-xl p-6`}>
          <p className={theme.textError}>Error loading analytics: {error}</p>
        </div>
      </div>
    );
  }

  const periods = userGrowth?.periods || [];
  const maxGrowthValue = userGrowth?.max_count && userGrowth.max_count > 0 ? userGrowth.max_count : 1;
  // One label per bar up to 8 bars; beyond that, thin them out evenly so
  // labels never crowd or silently truncate to a fixed count regardless of
  // how much data actually came back.
  const labelStride = periods.length > 8 ? Math.ceil(periods.length / 8) : 1;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex justify-end">
        <div className="flex space-x-2">
          {(['day', 'week', 'month'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`py-2 px-4 rounded-xl transition-all duration-300 text-xs font-medium ${period === p ? `${theme.buttonPrimary} ${theme.buttonTextPrimary} shadow-lg` : theme.buttonGhost} hover:scale-105 active:scale-95`}
            >
              {p === 'day' ? 'Daily' : p === 'week' ? 'Weekly' : 'Monthly'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${theme.card}`}>
          <h3 className={`text-sm font-medium mb-2 ${theme.textMuted} tracking-wide`}>Total Users</h3>
          <p className={`text-4xl font-bold mb-3 ${theme.textHeading} tracking-tight`}>
            {userCount ? userCount.total.toLocaleString() : '—'}
          </p>
          {userCount && (
            <div className="flex items-center text-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 mr-1 ${userCount.growth >= 0 ? theme.alertSuccess : theme.alertError}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={userCount.growth >= 0 ? "M5 10l7-7m0 0l7 7m-7-7v18" : "M19 14l-7 7m0 0l-7-7m7 7V3"} />
              </svg>
              <span className={`font-medium ${userCount.growth >= 0 ? theme.alertSuccess : theme.alertError}`}>
                {userCount.growth >= 0 ? '+' : ''}{userCount.growth.toFixed(1)}%
              </span>
              <span className={`ml-1 ${theme.textSecondary}`}>vs last {period}</span>
            </div>
          )}
        </div>
        <div className={`rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${theme.card}`}>
          <h3 className={`text-sm font-medium mb-2 ${theme.textMuted} tracking-wide`}>Active Sessions</h3>
          <p className={`text-4xl font-bold mb-3 ${theme.textHeading} tracking-tight`}>
            {activeSessions ? activeSessions.active_sessions.toLocaleString() : '—'}
          </p>
          {activeSessions && (
            <p className={`text-sm ${theme.textSecondary}`}>
              Avg {Math.round(activeSessions.average_session_duration_minutes || 0)} min session
            </p>
          )}
        </div>
        <div className={`rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${theme.card}`}>
          <h3 className={`text-sm font-medium mb-2 ${theme.textMuted} tracking-wide`}>Unique Visitors</h3>
          <p className={`text-4xl font-bold mb-3 ${theme.textHeading} tracking-tight`}>
            {visitors ? visitors.unique_visitors.toLocaleString() : '—'}
          </p>
          {visitors && (
            <p className={`text-sm ${theme.textSecondary}`}>
              {visitors.total_page_views.toLocaleString()} page views this {period}
            </p>
          )}
        </div>
      </div>

      <div className={`rounded-2xl p-8 transition-all duration-300 hover:shadow-2xl ${theme.card}`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-2xl font-bold ${theme.textHeading} tracking-tight`}>User Growth</h2>
          {userGrowth && (
            <div className={`text-sm ${theme.textSecondary}`}>
              {userGrowth.total_new_users.toLocaleString()} new users · avg {userGrowth.average_growth_rate >= 0 ? '+' : ''}{userGrowth.average_growth_rate.toFixed(1)}% per period
            </div>
          )}
        </div>
        {periods.length > 0 ? (
          <>
            <div className="h-60 flex items-end justify-between gap-1">
              {periods.map((item, index) => {
                const height = (item.user_count / maxGrowthValue) * 100;
                return (
                  <div
                    key={index}
                    className={`flex-1 rounded-t-lg transition-all duration-300 ${theme.buttonPrimary}`}
                    style={{ height: `${Math.max(height, 5)}%` }}
                    title={`${item.period_label}: ${item.user_count.toLocaleString()} users (+${item.new_users})`}
                  />
                );
              })}
            </div>
            <div className={`flex justify-between text-xs mt-2 ${theme.textMuted}`}>
              {periods.map((item, index) =>
                index % labelStride === 0 ? <div key={index}>{item.period_label}</div> : null
              )}
            </div>
          </>
        ) : (
          <div className="h-60 flex items-center justify-center">
            <p className={theme.textMuted}>No growth data available yet</p>
          </div>
        )}
      </div>

      <div className={`rounded-2xl p-8 transition-all duration-300 hover:shadow-2xl ${theme.card}`}>
        <h2 className={`text-2xl font-bold mb-6 ${theme.textHeading} tracking-tight`}>Traffic Sources</h2>
        {trafficSources && trafficSources.length > 0 ? (
          <div className="space-y-4">
            {trafficSources.map((source) => (
              <div key={source.source_type}>
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-sm font-medium capitalize ${theme.textPrimary}`}>
                    {source.source_type || 'Direct'}
                  </span>
                  <span className={`text-sm ${theme.textSecondary}`}>
                    {source.page_views.toLocaleString()} views · {source.percentage.toFixed(1)}%
                  </span>
                </div>
                <div className={`h-2 rounded-full overflow-hidden ${theme.progressTrack}`}>
                  <div
                    className={`h-full rounded-full ${theme.progressFill}`}
                    style={{ width: `${Math.max(source.percentage, 2)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={theme.textMuted}>No traffic source data available yet</p>
        )}
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
